<script lang="ts" setup>
// //////////////////////////////////////////////////////////////////////
// /////////////////////////// IMPORTS ////////////////////////////////
// //////////////////////////////////////////////////////////////////////

import { ref } from "vue";
import { createPriceLayerStore, PriceLayer } from "@/data/entities";
import {
  Filter,
  FilterConfig,
  TableConfig,
  TableRow,
} from "@/components/tables/types.d";
import { dialogEvents } from "..";
import { it } from "node:test";

// //////////////////////////////////////////////////////////////////////
// /////////////////////////// SETUP ////////////////////////////////
// //////////////////////////////////////////////////////////////////////

const priceLayerStore = createPriceLayerStore();

// //////////////////////////////////////////////////////////////////////
// /////////////////////////// VARIABLES /////////////////////////////
// //////////////////////////////////////////////////////////////////////

const columns = ref([
  {
    title: "Name",
    key: "data.name",
    sortable: true,
    property: "name",
  },
  {
    title: "Description",
    key: "data.description",
    property: "description",
  },
]);

const filters = ref<(FilterConfig | Filter)[]>([]);

const loading = ref(false);

const isVisible = ref(false);

const selectCallback = ref<(item: PriceLayer) => void>(
  (item: PriceLayer) => {},
);

const defaultActions = ref({
  new: {
    icon: "tabler-plus",
    label: "New",
    color: "primary",
    handler: newItem,
  },
});

/**
 * Navigates to the "priceLayer-form" route.
 */
function newItem(done?: () => void): void {
  // Select a new item
  selectCallback.value(new PriceLayer());
  isVisible.value = false;

  done && done();
}

/**
 * Transform a priceLayer to a TableRow
 * @param item PriceLayer
 */
function toTableRow(item: PriceLayer): TableRow {
  return {
    id: item.id,
    data: item,
    formatted: {},
    actions: {
      select: {
        icon: "tabler-arrow-right",
        handler: selectItem,
      },
    },
  };
}

/**
 * Edit a PriceLayer
 * @param item TableRow
 */
async function selectItem(item: TableRow, done?: () => void): Promise<void> {
  // Select the price layer
  selectCallback.value(item.data as PriceLayer);
  isVisible.value = false;

  done && done();
}

const multiItemsActions = ref({});

const rowClickHandler = (event: Event, row: { item: TableRow }) => {
  if (row.item) {
    selectItem(row.item);
  }
};

const tableConfig = ref<TableConfig>({
  key: "PriceLayerSelectDialog",
  entityName: "PriceLayer",
  store: priceLayerStore,
  toTableRow: toTableRow,
  columns: columns.value,
  filtersConfig: filters.value,
  defaultActions: defaultActions.value,
  multiItemsActions: multiItemsActions.value,
  hideSelect: true,
  disableSearch: true,
  rowClickHandler: rowClickHandler,
});

onMounted(() => {
  window.addEventListener(
    dialogEvents.PriceLayerSelectDialog.OPEN,
    ({ detail }: any) => {
      // Load
      selectCallback.value = detail.callback;

      // Set the visibility to isVisible
      isVisible.value = true;
    },
  );
  window.addEventListener(dialogEvents.PriceLayerSelectDialog.CLOSE, () => {
    // Set the visibility to hidden
    isVisible.value = false;
  });
});
</script>

<template>
  <TableDialog
    :config="tableConfig"
    v-model:is-visible="isVisible"
    :loading="loading"
    :hide-actions="true"
    width="90vw"
  />
</template>
